import { createSelector } from '@reduxjs/toolkit';
import i18next from 'i18next';
import { AppState } from '../../common/models/AppState';
import { VerificationFlag, SignatureFlag, SignatureScope } from '../../common/models/Transaction';
import { calculateStrategy } from './strategy';

// Root
export function selectForm(state: AppState) {
  return state.form;
}

export function selectFormTransaction(state: AppState) {
  return selectForm(state).transaction;
}

export const selectIsFormVerified = createSelector(
  [(state: AppState) => selectFormTransaction(state)?.meta?.verification?.customer1],
  (verification) => ({
    isVerifiedPhone: verification?.sms === VerificationFlag.VERIFIED,
    isVerifiedEmail: verification?.email === VerificationFlag.VERIFIED,
  }),
);

const EMPTY_SIGNATURES: Partial<Record<SignatureScope, SignatureFlag>> = {};

export function selectFormSignatures(state: AppState) {
  return selectFormTransaction(state)?.meta?.signature?.customer1?.sms ?? EMPTY_SIGNATURES;
}

// Informations
export function selectFormInformations(state: AppState) {
  return selectForm(state).informations;
}

export function selectFormInformationsDone(state: AppState) {
  return !Object.entries(selectForm(state).informations).some(
    ([key, value]) => key !== 'customerInfo2' && value === null,
  );
}

export function selectInformationsCompanyInfo(state: AppState) {
  return selectFormInformations(state).companyInfo;
}

export function selectInformationsCustomerInfo1(state: AppState) {
  return selectFormInformations(state).customerInfo1;
}

export function selectInformationsCustomer1FullName(state: AppState) {
  const { firstName, lastName, title } = selectInformationsCustomerInfo1(state) ?? {};
  return [title, firstName, lastName].join(' ').trim();
}

export function selectInformationsCustomerInfo2(state: AppState) {
  return selectFormInformations(state).customerInfo2;
}

export function selectIsCustomerInfo2Visible(state: AppState) {
  return selectInformationsCustomerInfo2(state) !== null;
}

// Investing
export function selectFormInvesting(state: AppState) {
  return selectForm(state).investing;
}

export function selectInvestingExperience(state: AppState) {
  return selectFormInvesting(state).experience;
}

export function selectInvestingHazard(state: AppState) {
  return selectFormInvesting(state).hazard;
}

export function selectInvestingFinances(state: AppState) {
  return selectFormInvesting(state).finances;
}

export function selectInvestingAnalyse(state: AppState) {
  return selectFormInvesting(state).analyse;
}

export function selectInvestingAgreements(state: AppState) {
  return selectFormInvesting(state).agreements;
}

export function selectInvestingStrategy(state: AppState) {
  return selectFormInvesting(state).strategy;
}

export function selectInvestingDone(state: AppState) {
  return !Object.entries(selectForm(state).investing).some((entry) => entry[1] === null);
}

export const selectStrategy = createSelector([selectFormInvesting, () => i18next.language], (investing) => {
  const strategy = investing.strategy || calculateStrategy(investing);

  return {
    name: i18next.t(`strategies.${strategy}`),
    type: strategy,
  };
});

// Protocol
export function selectFormProtocol(state: AppState) {
  return selectForm(state).protocol;
}

export function selectProtocolGoals(state: AppState) {
  return selectFormProtocol(state).goals;
}

export function selectProtocolInitiation(state: AppState) {
  return selectFormProtocol(state).initiation;
}

export function selectProtocolCustomerStatement(state: AppState) {
  return selectFormProtocol(state).customerStatement;
}

export function selectProtocolCustomerConfirmation(state: AppState) {
  return selectFormProtocol(state).customerConfirmation;
}

export function selectProtocolConsultantConfirmation(state: AppState) {
  return selectFormProtocol(state).consultantConfirmation;
}

export function selectProtocolDone(state: AppState) {
  return !Object.entries(selectForm(state).protocol).some((entry) => entry[1] === null);
}

// Agreement
export function selectFormAgreement(state: AppState) {
  return selectForm(state).agreement;
}

export function selectAgreementRequestInfo(state: AppState) {
  return selectFormAgreement(state).requestInfo;
}

export function selectAgreementBusinessPartnerInfo(state: AppState) {
  return selectFormAgreement(state).businessPartnerInfo;
}

export function selectAgreementMeeting(state: AppState) {
  return selectFormAgreement(state).meeting;
}

export function selectAgreementDone(state: AppState) {
  return !Object.entries(selectForm(state).agreement).some((entry) => entry[1] === null);
}

// Best of Templeton
export function selectBestOfTempletonAccountInfo(state: AppState) {
  return selectForm(state).bestOfTempleton;
}

export function selectBestOfTempletonDone(state: AppState) {
  return selectForm(state).bestOfTempleton !== null;
}
